<script setup lang="ts">
/**
 * Pre-launch store buttons — App Store + Google Play shown but blocked
 * by an angled "Coming soon" ribbon. Clicking the ribbon opens a modal
 * email-me-when-it-launches form (POST /api/subscribe → Resend audience).
 */
import { ref } from 'vue';
import { IconBrandApple, IconBrandGooglePlay, IconCheck, IconMail, IconX } from '@tabler/icons-vue';

defineProps<{ size?: 'sm' | 'md' }>();

const formOpen = ref(false);
const email = ref('');
const state = ref<'idle' | 'sending' | 'done' | 'error'>('idle');

function open(): void {
  formOpen.value = true;
  if (state.value !== 'done') state.value = 'idle';
}

function close(): void {
  formOpen.value = false;
}

async function submit(): Promise<void> {
  const value = email.value.trim();
  if (!value || state.value === 'sending') return;
  state.value = 'sending';
  try {
    const res = await fetch('/api/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: value }),
    });
    state.value = res.ok ? 'done' : 'error';
  } catch {
    state.value = 'error';
  }
}
</script>

<template>
  <div class="coming-soon-stores" :class="size === 'sm' ? 'sm' : 'md'">
    <div class="store-wrap">
      <a class="store-btn" aria-disabled="true" tabindex="-1">
        <IconBrandApple :size="size === 'sm' ? 15 : 17" stroke-width="1.75" />
        App Store
      </a>
      <a class="store-btn" aria-disabled="true" tabindex="-1">
        <IconBrandGooglePlay :size="size === 'sm' ? 15 : 17" stroke-width="1.75" />
        Google Play
      </a>
      <button type="button" class="ribbon" @click="open">Coming soon</button>
    </div>

    <Teleport to="body">
      <div v-if="formOpen" class="notify-backdrop" @click.self="close">
        <div class="notify-modal" role="dialog" aria-modal="true" aria-label="Get notified at launch">
          <button class="close-btn" aria-label="Close" @click="close">
            <IconX :size="16" stroke-width="2" />
          </button>
          <h3>Coming soon</h3>
          <template v-if="state !== 'done'">
            <p class="notify-lead">
              The app isn't out yet — leave your email and we'll let you know the moment it
              launches.
            </p>
            <form class="notify-form" @submit.prevent="submit">
              <input
                v-model="email"
                type="email"
                required
                placeholder="you@example.com"
                aria-label="Email address"
                :disabled="state === 'sending'"
              />
              <button type="submit" :disabled="state === 'sending'">
                <IconMail :size="14" stroke-width="1.75" />
                {{ state === 'sending' ? 'Sending…' : 'Email me when the app launches' }}
              </button>
            </form>
            <p v-if="state === 'error'" class="notify-error">Something went wrong — please try again.</p>
            <p class="notify-note">
              We'll only use your email to tell you when the app launches — nothing else, and you
              can unsubscribe with one click.
            </p>
          </template>
          <p v-else class="notify-done">
            <IconCheck :size="15" stroke-width="2" />
            You're on the list — we'll be in touch at launch.
          </p>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.store-wrap {
  position: relative;
  display: flex;
  gap: 8px;
}

.store-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: var(--accent);
  color: var(--accent-contrast);
  font-weight: 600;
  text-decoration: none;
  border-radius: 8px;
  opacity: 0.35;
  pointer-events: none;
  cursor: default;
}

.md .store-btn {
  min-height: 42px;
  font-size: 13px;
}

.sm .store-btn {
  min-height: 36px;
  font-size: 12px;
}

.ribbon {
  position: absolute;
  inset: 0;
  margin: auto;
  width: max-content;
  height: max-content;
  z-index: 2;
  transform: rotate(-5deg);
  padding: 5px 16px;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--accent-contrast);
  background: var(--text-primary);
  border: none;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
  cursor: pointer;
  transition: transform 0.12s;
}

.ribbon:hover {
  transform: rotate(-5deg) scale(1.05);
}

.notify-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(8, 8, 16, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 300;
}

.notify-modal {
  position: relative;
  width: min(380px, calc(100vw - 32px));
  background: var(--ui-surface);
  border: 1px solid var(--ui-border);
  border-radius: 12px;
  padding: 22px;
}

.notify-modal h3 {
  margin: 0 0 8px;
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-primary);
}

.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--text-muted);
  cursor: pointer;
}

.close-btn:hover {
  color: var(--text-primary);
}

.notify-lead {
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.55;
  color: var(--text-muted);
}

.notify-form {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.notify-form input {
  min-height: 32px;
  padding: 0 10px;
  font-size: 12.5px;
  color: var(--text-primary);
  background: var(--canvas-bg);
  border: 1px solid var(--ui-border);
  border-radius: 8px;
}

.notify-form input:focus {
  outline: none;
  border-color: var(--accent);
}

.notify-form button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 32px;
  font-size: 12px;
  font-weight: 600;
  color: var(--accent-contrast);
  background: var(--accent);
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

.notify-form button:disabled {
  opacity: 0.6;
  cursor: default;
}

.notify-note {
  margin: 6px 0 0;
  font-size: 10.5px;
  line-height: 1.5;
  color: var(--text-muted);
}

.notify-error {
  margin: 6px 0 0;
  font-size: 11.5px;
  color: var(--danger);
}

.notify-done {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 12px;
  color: var(--text-primary);
}
</style>
